import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Announcement, getFormattedLongDate, parseLocalDate, ProgressBar } from '@utilities';

import styles from './_index.module.scss';

const SubscriptionOverview = ({ subscription: subscriptionProp }) => {
  const userData = useSelector((state) => state?.user?.data);
  const clientInfo = userData?.clientInfo;
  const reduxSubscription = clientInfo?.subscriptionDetails?.find(
    (sub) => sub.id === clientInfo?.subscriptions?.[0]?.subscriptionId
  );

  const subscription = subscriptionProp || reduxSubscription;

  if (!subscription) {
    return (
      <div>
        <Announcement
          header="No subscription information available."
          text="Please contact your Numerator team for more information."
          variant="info"
        />
      </div>
    );
  }

  if (!subscription?.hasStarted) {
    return (
      <div>
        <Announcement
          header={`Great news! Your subscription kicks off ${getFormattedLongDate(parseLocalDate(subscription.startDate))}`}
          text="Please contact your Numerator team for more information."
          variant="info"
        />
      </div>
    );
  }

  const totalCompletes = subscription?.currentPeriod?.totalCompletes ?? 0;
  const usedCompletes = subscription?.currentPeriod?.usedCompletes ?? 0;
  const remainingCompletes = Math.max(0, totalCompletes - usedCompletes);

  // Calculate next reset date: if subscription end date is after current period end date,
  // the next reset is 1 day after the current period ends
  const currentPeriodEnd = subscription?.currentPeriod?.endDate;
  const hasNextPeriod = subscription?.hasFuturePeriods;

  const nextResetDate = hasNextPeriod
    ? (() => {
        if (!currentPeriodEnd) {
          return null;
        }
        const nextDate = parseLocalDate(currentPeriodEnd);
        nextDate.setDate(nextDate.getDate() + 1);
        return nextDate;
      })()
    : null;

  return (
    <div className={styles['subscription-overview-usage-row-container']}>
      <section className={styles['subscription-overview-usage-row']}>
        <span>Completes</span>
        <span>
          <strong>
            {usedCompletes.toLocaleString()} / {totalCompletes.toLocaleString()} used
          </strong>
        </span>
      </section>
      <ProgressBar
        className={styles['subscription-overview-bar-neutral']}
        hasError={totalCompletes > 0 && usedCompletes / totalCompletes >= 1}
        max={totalCompletes}
        value={usedCompletes}
      />
      <p className={styles['subscription-overview-remaining']}>
        {remainingCompletes.toLocaleString()} remaining
      </p>
      <ul className={styles['subscription-overview-resets']}>
        <li>
          Period: {getFormattedLongDate(parseLocalDate(subscription?.currentPeriod?.startDate))} -{' '}
          {getFormattedLongDate(parseLocalDate(subscription?.currentPeriod?.endDate))}
        </li>
        {hasNextPeriod && (
          <li>Next reset: {nextResetDate ? getFormattedLongDate(nextResetDate) : 'N/A'}</li>
        )}
      </ul>
    </div>
  );
};

SubscriptionOverview.propTypes = {
  subscription: PropTypes.object,
};

export default SubscriptionOverview;
